// src/components/MarketResearch/ReportContent.tsx
import React, { useState } from 'react';
import {
  Box,
  Alert,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  AccessTime as AccessTimeIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { ExtendedMarketResearch } from '../types';

import { ExecutiveSummary } from './sections/ExecutiveSummary';
import { MarketOverview } from './sections/MarketOverview';
import { CompetitiveAnalysis } from './sections/CompetitiveAnalysis';
import { SupplyAnalysis } from './sections/SupplyAnalysis';
import { EconomicIndicators } from './sections/EconomicIndicators';
import { Recommendations } from './sections/Recommendations';
import { Footer } from './sections/Footer';

interface Props {
  marketResearch: ExtendedMarketResearch[];
  error: string | null;
  loading: boolean;
  reportRef: React.RefObject<HTMLDivElement | null>;
  onDeleteReport?: (reportId: string | number) => void;
}

export const ReportContent: React.FC<Props> = ({
  marketResearch,
  error,
  loading,
  reportRef,
  onDeleteReport
}) => {
  const theme = useTheme();
  const [expandedReports, setExpandedReports] = useState<(string | number)[]>(() => {
    // Initialize with the first report expanded
    return marketResearch.length > 0 && marketResearch[0].id
      ? [marketResearch[0].id]
      : [];
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<string | number | null>(null);

  const formatDate = (dateString?: string) =>
    dateString
      ? new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
      : 'Recent Analysis';

  const handleAccordionChange = (reportId: string | number) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedReports(prev =>
      isExpanded
        ? [...prev, reportId]
        : prev.filter(id => id !== reportId)
    );
  };

  const handleDeleteClick = (reportId: string | number, event: React.MouseEvent) => {
    event.stopPropagation();
    setReportToDelete(reportId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (reportToDelete && onDeleteReport) {
      onDeleteReport(reportToDelete);
    }
    setDeleteDialogOpen(false);
    setReportToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setReportToDelete(null);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }
  if (marketResearch.length === 0 && !loading) {
    return (
      <Alert severity="info" sx={{ py: 2 }}>
        No market research available. Generate new research to analyze this hotel's market.
      </Alert>
    );
  }

  return (
    <>
      {marketResearch.map((r, i) => (
        <Accordion
          key={r.id || i}
          expanded={expandedReports.includes(r.id || i)}
          onChange={handleAccordionChange(r.id || i)}
          sx={{
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: '0 0 16px 0',
            },
            boxShadow: theme.shadows[1],
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: i === 0
                ? alpha(theme.palette.primary.main, 0.04)
                : theme.palette.background.paper,
              borderRadius: '8px 8px 0 0',
              '&.Mui-expanded': {
                minHeight: 64,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                mr: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon
                  sx={{
                    color: i === 0 ? theme.palette.primary.main : theme.palette.text.secondary
                  }}
                />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Market Research Report {i === 0 && (
                      <Chip
                        label="Latest"
                        size="small"
                        color="primary"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                    <Typography variant="caption" color="text.secondary">
                      Generated: {formatDate(r.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {onDeleteReport && (
                <IconButton
                  onClick={(e) => handleDeleteClick(r.id || i, e)}
                  size="small"
                  sx={{
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <Box
              ref={i === 0 ? reportRef : undefined}
              className="print-container"
              sx={{
                '@media print': {
                  '& > *:not(:last-child)': { mb: 4 },
                  color: '#000000',
                },
              }}
            >
              <ExecutiveSummary summary={r.research_data.marketOverview?.summary || ''} />
              <MarketOverview data={r.research_data.marketOverview} />
              <CompetitiveAnalysis data={r.research_data.competitiveAnalysis} />
              <SupplyAnalysis data={r.research_data.supplyAnalysis} />
              <EconomicIndicators data={r.research_data.economicIndicators} />

              {/* Safe rendering of recommendations with error logging */}
              {(() => {
                try {
                  const recommendationsData = r.research_data.recommendations;
                  console.log('ReportContent - recommendations data:', recommendationsData);
                  return <Recommendations data={recommendationsData} />;
                } catch (error) {
                  console.error('Error rendering Recommendations:', error);
                  return (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      Error rendering recommendations section
                    </Alert>
                  );
                }
              })()}

              <Footer reportId={r.id} />
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Market Research Report
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this market research report? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
